/**
 * Core Scanner Module
 * Works with both file system (Node.js CLI) and file content (Browser)
 */

import type { ImportInfo, ExportInfo, ScannedFile, ScanResult, ProgressCallback } from './types';

// File extensions to scan
export const CODE_EXTENSIONS = new Set([
  '.js', '.jsx', '.ts', '.tsx', '.mjs', '.cjs',
  '.vue', '.svelte', '.astro',
  '.css', '.scss', '.sass', '.less',
  '.json', '.md', '.mdx'
]);

// Directories to skip
export const SKIP_DIRS = new Set([
  'node_modules', '.git', '.next', 'dist', 'build',
  '.cache', 'coverage', '.turbo', '.vercel',
  'typechain-types', 'artifacts', 'cache'
]);

/**
 * Get file extension from path
 */
export function getExtension(filePath: string): string {
  const match = filePath.match(/\.[^.\/\\]+$/);
  return match ? match[0].toLowerCase() : '';
}

/**
 * Get directory from path
 */
export function getDirectory(filePath: string): string {
  const parts = filePath.replace(/\\/g, '/').split('/');
  parts.pop();
  return parts.join('/') || '.';
}

/**
 * Get filename from path
 */
export function getFilename(filePath: string): string {
  const parts = filePath.replace(/\\/g, '/').split('/');
  return parts[parts.length - 1] || filePath;
}

/**
 * Extract imports from file content
 */
export function extractImports(content: string, filePath: string): ImportInfo[] {
  const imports: ImportInfo[] = [];
  const ext = getExtension(filePath);

  // Skip non-code files for import extraction
  if (!['.js', '.jsx', '.ts', '.tsx', '.mjs', '.cjs', '.vue', '.svelte'].includes(ext)) {
    return imports;
  }

  // ES6 imports using matchAll
  const esImportPattern = /import\s+(?:(?:(\{[^}]*\})|(\*\s+as\s+\w+)|(\w+))(?:\s*,\s*(?:(\{[^}]*\})|(\*\s+as\s+\w+)|(\w+)))*\s+from\s+)?['"`]([^'"`]+)['"`]/g;
  for (const match of content.matchAll(esImportPattern)) {
    const importPath = match[7] || match[0].match(/['"`]([^'"`]+)['"`]/)?.[1];
    if (importPath) {
      const namedImports: string[] = [];
      const braceMatch = match[1] || match[4];
      if (braceMatch) {
        const names = braceMatch.replace(/[{}]/g, '').split(',').map(s => s.trim().split(/\s+as\s+/)[0].trim()).filter(Boolean);
        namedImports.push(...names);
      }
      const defaultImport = match[3] || match[6];
      const namespaceImport = match[2] || match[5];

      imports.push({
        type: 'es6',
        source: importPath,
        resolved: null,
        defaultImport: defaultImport || null,
        namedImports,
        namespaceImport: namespaceImport ? namespaceImport.replace(/\*\s+as\s+/, '') : null,
        line: content.substring(0, match.index).split('\n').length
      });
    }
  }

  // Type imports
  const typeImportPattern = /import\s+type\s+(?:(\{[^}]*\})|(\w+))\s+from\s+['"`]([^'"`]+)['"`]/g;
  for (const match of content.matchAll(typeImportPattern)) {
    const namedImports: string[] = [];
    if (match[1]) {
      const names = match[1].replace(/[{}]/g, '').split(',').map(s => s.trim().split(/\s+as\s+/)[0].trim()).filter(Boolean);
      namedImports.push(...names);
    }
    imports.push({
      type: 'type',
      source: match[3],
      resolved: null,
      defaultImport: match[2] || null,
      namedImports,
      namespaceImport: null,
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  // Dynamic imports
  const dynamicImportPattern = /import\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/g;
  for (const match of content.matchAll(dynamicImportPattern)) {
    imports.push({
      type: 'dynamic',
      source: match[1],
      resolved: null,
      defaultImport: null,
      namedImports: [],
      namespaceImport: null,
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  // CommonJS require
  const requirePattern = /(?:const|let|var)\s+(?:(\{[^}]*\})|(\w+))\s*=\s*require\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/g;
  for (const match of content.matchAll(requirePattern)) {
    const namedImports: string[] = [];
    if (match[1]) {
      const names = match[1].replace(/[{}]/g, '').split(',').map(s => s.trim()).filter(Boolean);
      namedImports.push(...names);
    }
    imports.push({
      type: 'commonjs',
      source: match[3],
      resolved: null,
      defaultImport: match[2] || null,
      namedImports,
      namespaceImport: null,
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  // Re-exports
  const reExportPattern = /export\s+(?:\*|\{[^}]*\})\s+from\s+['"`]([^'"`]+)['"`]/g;
  for (const match of content.matchAll(reExportPattern)) {
    imports.push({
      type: 'es6',
      source: match[1],
      resolved: null,
      defaultImport: null,
      namedImports: [],
      namespaceImport: null,
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  return imports;
}

/**
 * Extract exports from file content
 */
export function extractExports(content: string, filePath: string): ExportInfo[] {
  const exports: ExportInfo[] = [];
  const ext = getExtension(filePath);

  if (!['.js', '.jsx', '.ts', '.tsx', '.mjs', '.cjs'].includes(ext)) {
    return exports;
  }

  // Named exports
  const namedExportPattern = /export\s+(?:const|let|var|function|class|interface|type|enum|async\s+function)\s+(\w+)/g;
  for (const match of content.matchAll(namedExportPattern)) {
    exports.push({
      type: 'named',
      name: match[1],
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  // Default export with name
  const defaultExportPattern = /export\s+default\s+(?:function\s+(\w+)|class\s+(\w+)|(\w+))/g;
  for (const match of content.matchAll(defaultExportPattern)) {
    const name = match[1] || match[2] || match[3];
    exports.push({
      type: 'default',
      name: name || 'default',
      line: content.substring(0, match.index!).split('\n').length
    });
  }

  // Check for anonymous default export
  if (!exports.some(e => e.type === 'default')) {
    if (/export\s+default\s+(?:function|class|\(|{|\[)/.test(content)) {
      exports.push({
        type: 'default',
        name: 'default',
        line: 0
      });
    }
  }

  // Export statements like: export { foo, bar }
  const exportListPattern = /export\s+\{([^}]+)\}(?!\s+from)/g;
  for (const match of content.matchAll(exportListPattern)) {
    const names = match[1].split(',').map(s => {
      const parts = s.trim().split(/\s+as\s+/);
      return parts[parts.length - 1].trim();
    }).filter(Boolean);
    for (const name of names) {
      exports.push({
        type: 'named',
        name,
        line: content.substring(0, match.index!).split('\n').length
      });
    }
  }

  return exports;
}

/**
 * Normalize path separators to forward slashes
 */
export function normalizePath(p: string): string {
  return p.replace(/\\/g, '/');
}

/**
 * Resolve import path relative to a file
 * Works with a file map (browser) or can be extended for fs (CLI)
 */
export function resolveImportPath(
  importSource: string,
  currentFilePath: string,
  fileMap: Map<string, string>,
  rootDir: string = ''
): { resolved: string | null; isExternal: boolean; package?: string } {
  // Skip external packages (don't start with . or / or common aliases)
  if (!importSource.startsWith('.') && !importSource.startsWith('/') && !importSource.startsWith('@/') && !importSource.startsWith('~')) {
    // Handle scoped packages like @org/package
    const pkg = importSource.startsWith('@')
      ? importSource.split('/').slice(0, 2).join('/')
      : importSource.split('/')[0];
    return { resolved: null, isExternal: true, package: pkg };
  }

  const currentDir = getDirectory(normalizePath(currentFilePath));

  // Resolve the target path
  let targetPath = resolveRelativePath(importSource, currentDir, rootDir);

  // Try various extensions and index files
  const extensions = [
    '',
    '.ts', '.tsx', '.js', '.jsx', '.mjs', '.cjs',
    '.vue', '.svelte',
    '/index.ts', '/index.tsx', '/index.js', '/index.jsx', '/index.mjs',
    '.d.ts'
  ];

  for (const ext of extensions) {
    const fullPath = targetPath + ext;
    if (fileMap.has(fullPath)) {
      return { resolved: fullPath, isExternal: false };
    }
  }

  // Try without leading slash if present
  if (targetPath.startsWith('/')) {
    const withoutSlash = targetPath.slice(1);
    for (const ext of extensions) {
      const fullPath = withoutSlash + ext;
      if (fileMap.has(fullPath)) {
        return { resolved: fullPath, isExternal: false };
      }
    }
  }

  // Try finding by filename match (for cases where directory structure differs)
  const targetName = targetPath.split('/').pop() || targetPath;
  if (targetName && !targetName.includes('.')) {
    for (const [filePath] of fileMap) {
      const fileName = filePath.split('/').pop() || '';
      const baseName = fileName.replace(/\.(ts|tsx|js|jsx|mjs|cjs|vue|svelte)$/, '');
      if (baseName === targetName) {
        // Check if it's in a plausible location (same parent directory structure)
        const targetParts = targetPath.split('/').filter(Boolean);
        const fileParts = filePath.split('/').filter(Boolean);
        if (targetParts.length > 0 && fileParts.length > 0) {
          // Match if the last N parts align
          const matchDepth = Math.min(targetParts.length, fileParts.length);
          let matches = true;
          for (let i = 1; i <= matchDepth; i++) {
            const targetPart = targetParts[targetParts.length - i];
            const filePart = fileParts[fileParts.length - i]?.replace(/\.(ts|tsx|js|jsx|mjs|cjs|vue|svelte)$/, '');
            if (targetPart !== filePart && !(i === 1 && targetPart === filePart?.replace(/\.(ts|tsx|js|jsx|mjs|cjs|vue|svelte)$/, ''))) {
              matches = false;
              break;
            }
          }
          if (matches) {
            return { resolved: filePath, isExternal: false };
          }
        }
      }
    }
  }

  return { resolved: null, isExternal: false };
}

/**
 * Resolve a relative path against a base directory
 */
function resolveRelativePath(importSource: string, currentDir: string, rootDir: string): string {
  // Handle alias paths
  if (importSource.startsWith('@/')) {
    // Try common alias patterns: @/ -> src/, @/ -> ./
    return `src/${importSource.slice(2)}`;
  }

  if (importSource.startsWith('~/')) {
    return importSource.slice(2);
  }

  if (importSource.startsWith('/')) {
    return importSource.slice(1);
  }

  // Handle relative paths (./foo, ../foo, etc.)
  const segments: string[] = currentDir ? currentDir.split('/').filter(Boolean) : [];
  const importParts = importSource.split('/');

  for (const part of importParts) {
    if (part === '..') {
      segments.pop();
    } else if (part !== '.' && part !== '') {
      segments.push(part);
    }
  }

  return segments.join('/');
}

/**
 * Scan a single file from content
 */
export function scanFileContent(
  relativePath: string,
  content: string
): ScannedFile {
  const lines = content.split('\n');
  const ext = getExtension(relativePath);

  return {
    path: relativePath,
    relativePath,
    name: getFilename(relativePath),
    extension: ext,
    directory: getDirectory(relativePath),
    content,
    lineCount: lines.length,
    size: new Blob([content]).size,
    imports: extractImports(content, relativePath),
    exports: extractExports(content, relativePath)
  };
}

/**
 * Scan a collection of files (browser mode)
 * Takes a Map<relativePath, content>
 */
export function scanFileMap(
  fileMap: Map<string, string>,
  onProgress?: ProgressCallback
): ScanResult {
  const files: ScannedFile[] = [];
  const errors: Array<{ file: string; error: string }> = [];
  const total = fileMap.size;
  let current = 0;

  for (const [relativePath, content] of fileMap) {
    current++;
    if (onProgress) {
      onProgress({
        phase: 'scanning',
        current,
        total,
        message: `Scanning ${relativePath}`
      });
    }

    try {
      const scanned = scanFileContent(relativePath, content);

      // Resolve import paths
      scanned.imports = scanned.imports.map(imp => {
        const resolution = resolveImportPath(imp.source, relativePath, fileMap);
        return { ...imp, resolved: resolution.resolved };
      });

      files.push(scanned);
    } catch (err) {
      errors.push({ file: relativePath, error: (err as Error).message });
    }
  }

  return { files, errors };
}

/**
 * Filter files by extension
 */
export function shouldIncludeFile(filePath: string): boolean {
  const ext = getExtension(filePath);
  return CODE_EXTENSIONS.has(ext);
}

/**
 * Check if directory should be skipped
 */
export function shouldSkipDirectory(dirName: string): boolean {
  return SKIP_DIRS.has(dirName) || dirName.startsWith('.');
}
